/**
 * Abstract report visitor.
 *
 * Implement visiting methods for all report item classes.
 * All visiting method return the result of the visit of the object.
 *
 * The additional API method `visitAll` maps over a collection, visiting
 * each element.
 *
 * @abstract
 */
export default class ReportVisitor {
	visit(object) {
		return object.accept(this);
	}

	/**
	 * The default implementation returns a {columns, rows} object holding
	 * the result of the visit of each row and map separately.
	 */
	visitReport(report) {
		return {
			columns: this.visitAll(report.getColumns()),
			rows: this.visitAll(report.getRows()),
		};
	}

	/**
	 * Map over `collection`, visiting each element.
	 */
	visitAll(collection) {
		return collection.map((item) => this.visit(item));
	}

	visitAbstractColumn(column) {
		return this.visitAbstractItem(column);
	}

	visitAbstractPeriodColumn(column) {
		return this.visitAbstractColumn(column);
	}

	visitOpeningBalanceColumn(column) {
		return this.visitAbstractPeriodColumn(column);
	}

	visitClosingBalanceColumn(column) {
		return this.visitAbstractPeriodColumn(column);
	}

	visitAbstractForecastColumn(column) {
		return this.visitPeriodChangeColumn(column);
	}

	visitPeriodChangeColumn(column) {
		return this.visitAbstractPeriodColumn(column);
	}

	visitPredictedForecastColumn(column) {
		return this.visitAbstractForecastColumn(column);
	}

	visitCurrentPeriodForecastColumn(column) {
		return this.visitAbstractForecastColumn(column);
	}

	visitReferenceForecastColumn(column) {
		return this.visitAbstractForecastColumn(column);
	}

	visitYearToDateColumn(column) {
		return this.visitAbstractPeriodColumn(column);
	}

	visitRollingPeriodColumn(column) {
		return this.visitAbstractPeriodColumn(column);
	}

	visitDeviationColumn(column) {
		return this.visitAbstractColumn(column);
	}

	visitFormulaColumn(column) {
		return this.visitAbstractColumn(column);
	}

	visitPercentageColumn(column) {
		return this.visitAbstractColumn(column);
	}

	visitSeparatorColumn(column) {
		return this.visitAbstractColumn(column);
	}

	visitGroupColumn(column) {
		return this.visitAbstractColumn(column);
	}

	visitAbstractItem() {}

	visitAbstractRow(row) {
		return this.visitAbstractItem(row);
	}

	visitAbstractValueRow(row) {
		return this.visitAbstractRow(row);
	}

	visitAccountGroupRow(row) {
		return this.visitGroupRow(row);
	}

	visitGeneratedAccountGroupRow(row) {
		return this.visitAccountGroupRow(row);
	}

	visitAbstractAccountRow(row) {
		return this.visitAbstractValueRow(row);
	}

	visitAccountRow(row) {
		return this.visitAbstractAccountRow(row);
	}

	visitGeneratedAccountRow(row) {
		return this.visitAccountRow(row);
	}

	visitSpacerRow(row) {
		return this.visitAbstractRow(row);
	}

	visitTextRow(row) {
		return this.visitAbstractRow(row);
	}

	visitGroupRow(group) {
		return this.visitAbstractAccountRow(group);
	}

	visitRepeatingRow(group) {
		return this.visitGroupRow(group);
	}

	visitAccountIntervalGroupRow(row) {
		return this.visitGroupRow(row);
	}

	visitFormulaRow(row) {
		return this.visitAbstractValueRow(row);
	}

	visitKeyFigureRow(row) {
		return this.visitAbstractValueRow(row);
	}

	visitPreliminaryResultRow(row) {
		return this.visitKeyFigureRow(row);
	}

	visitStaticReportRow(row) {
		return this.visitAbstractRow(row);
	}
}
